import React, { useEffect, useMemo, useState } from "react";
import { Modal, message } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import useQuery from "../../../hooks/useQuery";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  NameCell,
  CellRow,
  TypeTag,
  BedTag,
  Tag,
  StatusPill,
  DimCell,
  NumCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import {
  getPharmacyOrdersListApi,
  cancelPharmacyOrderApi,
  canPrintOrderReceipt,
  printPharmacyOrderReceipt,
} from "../../../services/pharmacyOrders.services";
import { useBucketCounts } from "../common/bucketCounts";
import { formatMoney } from "../common/money";
import { orderValue } from "../common/lineMath";
import { getErrorMessage } from "../../../helpers/error";



/*
 * The buckets the band offers, which are not every status the server keeps.
 *
 * Partly dispensed and dispensed are the pharmacy's own stages of handling an
 * order rather than places an order sits waiting for someone - those rows still
 * list, and their status pill still names them; they are simply not a filter of
 * their own.
 */
const BUCKETS = [
  { key: "pending", label: "Pending", statuses: "pending", color: "#EAAA5E" },
  {
    key: "completed",
    label: "Completed",
    statuses: "completed",
    color: "#12B76A",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    statuses: "cancelled",
    color: "#98A2B3",
  },
];

/*
 * How an order's status reads, and the pill it reads in - background, text.
 *
 * The server keeps five; this screen shows three. Partly dispensed and
 * dispensed are the pharmacy's own stages of working an order, not places it
 * has got to: until it is billed it is still pending, and that is what the
 * column says. The server's own word stays on the pill's tooltip, so nothing
 * is hidden from anyone looking for it.
 */
const STATUS_WORD = {
  pending: "Pending",
  partially_dispensed: "Pending",
  dispensed: "Pending",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_TONE = {
  pending: ["#FFFAEB", "#B54708"],
  partially_dispensed: ["#FFFAEB", "#B54708"],
  dispensed: ["#FFFAEB", "#B54708"],
  completed: ["#ECFDF3", "#027A48"],
  cancelled: ["#F2F4F7", "#475467"],
};

/*
 * Cancelling is only honest while the medicines are still on the shelf. Once an
 * order is dispensed the stock has gone out with it, so taking it back is a
 * return, not a cancellation - and the row stops offering one.
 */
const CANCELLABLE = ["pending", "partially_dispensed"];

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

const inr0 = (value) =>
  formatMoney(Math.round(Number(value) || 0)).replace(".00", "");

/* "46 / M", or whichever half of it the record carries. */
const ageGender = (record) => {
  const gender = record.patient_gender || record.counter_sale_customer_gender;
  return (
    [record.patient_age, gender ? String(gender)[0].toUpperCase() : null]
      .filter((part) => part != null && part !== "")
      .join(" / ") || undefined
  );
};

const Orders = () => {
  const navigate = useNavigate();
  const query = useQuery();
  /*
   * The chosen bucket lives in the URL so returning from a sale entry lands
   * back where it was opened from, rather than resetting to everything.
   */
  const activeKey = BUCKETS.some((b) => b.key === query?.tab) ? query.tab : null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: PHARMACY_PAGE_SIZE };
    // No bucket means every status, which is what omitting ?tab= gives.
    const bucket = BUCKETS.find((b) => b.key === activeKey);
    if (bucket) params.tab = bucket.statuses;
    if (search) params.search = search;

    getPharmacyOrdersListApi(new URLSearchParams(params))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, activeKey, refresh]);


  const counts = useBucketCounts({
    buckets: BUCKETS,
    response: data,
    activeKey,
    search,
  });


  const rows = useMemo(() => data?.results || [], [data]);
  const total = Number(data?.count) || 0;
  /* Every order, whatever its status - which the band's own segments no longer
     cover, so they cannot simply be added up. The unfiltered list knows it. */
  const totalOrders =
    counts.ALL ?? BUCKETS.reduce((sum, b) => sum + (counts[b.key] || 0), 0);

  /*
   * The server's own count per status, and then whatever the three do not
   * cover.
   *
   * An order part-way through being dispensed is in none of the buckets, so
   * the three never added up to the list's total - and the band drew the
   * difference as a length of bar with no name against it, which reads as a
   * fourth state nobody will own up to. It is named instead: these are orders
   * being dispensed, and they are the one thing the other three are not.
   *
   * Not a filter, because the server does not offer one - it is counted and
   * labelled, and selecting it is left alone.
   */
  const named = BUCKETS.map((bucket) => ({
    ...bucket,
    count: counts[bucket.key] ?? 0,
  }));

  const accountedFor = named.reduce((sum, bucket) => sum + bucket.count, 0);
  const inProgress = Math.max(totalOrders - accountedFor, 0);

  const segments = inProgress
    ? [
        ...named,
        {
          key: "dispensing",
          label: "Dispensing",
          count: inProgress,
          color: "#3538CD",
        },
      ]
    : named;

  const askToCancel = (record) => {
    Modal.confirm({
      title: `Cancel ${record.order_number || "this order"}?`,
      content:
        "It stays on file as cancelled and leaves the pending queue. This cannot be undone.",
      okText: "Cancel order",
      okButtonProps: { danger: true },
      cancelText: "Keep it",
      onOk: () =>
        cancelPharmacyOrderApi(record.id)
          .then(() => {
            message.success(`${record.order_number || "Order"} cancelled`);
            const stillListed =
              !activeKey || activeKey === "cancelled";

            setData((prev) => {
              if (!prev) return prev;
              const results = (prev.results || [])
                .map((row) =>
                  row.id === record.id ? { ...row, status: "cancelled" } : row
                )
                .filter((row) => stillListed || row.id !== record.id);
              return {
                ...prev,
                results,
                count: stillListed
                  ? prev.count
                  : Math.max((Number(prev.count) || 1) - 1, 0),
              };
            });

            setRefresh((prev) => !prev);
          })
          .catch((err) => message.error(getErrorMessage(err))),
    });
  };

  /* The order as a document. `share=print` hands back the receipt itself, so
     this goes straight to the print dialog - where Save as PDF is. */
  const printReceipt = async (record) => {
    setPrintingId(record.id);
    try {
      await printPharmacyOrderReceipt(record.id, "detailed");
    } catch (err) {
      message.error(getErrorMessage(err) || "Unable to open the receipt");
    } finally {
      setPrintingId(null);
    }
  };

  const selectBucket = (key) => {
    /* Dispensing is a count, not a filter - the server has no bucket for it,
       so selecting it would ask for a tab that does not exist. */
    if (key === "dispensing") return;
    setPage(1);
    // Clicking the bucket already being shown clears the filter.
    const next = activeKey === key ? null : key;
    navigate(next ? `/pharmacy/orders?tab=${next}` : "/pharmacy/orders", {
      replace: true,
    });
  };

  const columns = [
    {
      title: "Order",
      dataIndex: "order_number",
      key: "order_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    {
      title: "Patient",
      key: "patient",
      render: (_, record) => (
        <NameCell
          name={record.patient_name || record.counter_sale_customer_name}
          sub={record.patient_mrn || record.counter_sale_customer_mobile}
        />
      ),
    },
    {
      title: "Type",
      key: "sale_type",
      render: (_, record) => (
        <CellRow>
          {/* Who the order is for and where they are, beside how it bills -
              the three things a ward order is picked out by. */}
          {ageGender(record) ? <Tag>{ageGender(record)}</Tag> : null}
          <TypeTag type={record.sale_type}>{record.sale_type || "-"}</TypeTag>
          {record.bed_no ? <BedTag>{record.bed_no}</BedTag> : null}
        </CellRow>
      ),
    },
    {
      title: "Items",
      key: "items",
      align: "right",
      render: (_, record) => <NumCell>{(record.items || []).length}</NumCell>,
    },
    {
      /*
       * What the order comes to - the one figure that says whether a row is
       * worth going into.
       *
       * The order's own total is used where the endpoint sends one; where it
       * does not, the lines are priced the way Sale Entry prices them. A
       * cancelled order shows what was called off rather than what it is worth,
       * so it reads as money going the other way.
       */
      title: "Amount",
      key: "amount",
      align: "right",
      render: (_, record) => {
        const stated =
          record.total_amount ?? record.grand_total ?? record.net_amount ?? record.amount;
        const amount = stated != null ? Number(stated) : orderValue(record);
        if (!amount) return <DimCell>-</DimCell>;

        const called_off = record.status === "cancelled";
        return (
          <NumCell>
            <b style={called_off ? { color: "#b42318" } : undefined}>
              {called_off ? `- ${inr0(amount)}` : inr0(amount)}
            </b>
          </NumCell>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <span title={prettyStatus(value)}>
          <StatusPill tone={STATUS_TONE[value] || STATUS_TONE.cancelled}>
            {STATUS_WORD[value] || prettyStatus(value)}
          </StatusPill>
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 128,
      render: (_, record) => (
        <RowActions>
          <IconAction
            icon="forms"
            label="View order"
            onClick={() =>
              navigate(
                `/pharmacy/orders/sale-entry/${record.id}${
                  activeKey ? `?from=${activeKey}` : ""
                }`
              )
            }
          />
          {/*
            * The receipt, on every row but live only once the order has been
            * submitted: until the pharmacy has supplied against it there is
            * nothing to draw, and a row that simply drops the control leaves
            * you wondering whether this order is different or the button moved.
            */}
          <IconAction
            icon="download"
            label={
              canPrintOrderReceipt(record)
                ? "Download receipt"
                : "Receipt is ready once this order is submitted"
            }
            disabled={!canPrintOrderReceipt(record) || printingId === record.id}
            onClick={() => printReceipt(record)}
          />
          {CANCELLABLE.includes(record.status) ? (
            <IconAction
              icon="close"
              tone="danger"
              label="Cancel order"
              onClick={() => askToCancel(record)}
            />
          ) : null}
        </RowActions>
      ),
    },
  ];


  return (
    <ListScreen>
      <ListHead
        title="Orders"
        actions={
          <>
            <UiButton onClick={() => navigate("/pharmacy/orders/sale-entry/new")}>
              <CgIcon name="plus" size={16} stroke={2.2} />
              Add Sale Entry
            </UiButton>
          </>
        }
      />

      <DistBand
        total={totalOrders}
        label="Orders"
        segments={segments}
        activeKey={activeKey}
        onSelect={selectBucket}
      />

      <ListCard
        title="Orders"
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          // ?search= covers the order number and the patient or counter-sale
          // customer's name and mobile, whichever the sale type stores.
          placeholder: "Search order number, patient or mobile",
        }}
        onClear={
          search || activeKey
            ? () => {
                setSearch("");
                setPage(1);
                navigate("/pharmacy/orders", { replace: true });
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={rows}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>
    </ListScreen>
  );
};

export default Orders;
